import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { AlertTriangle, BarChart3, CheckCircle2, Clock3, Copy, CreditCard, Download, ExternalLink, Eye, FileText, Loader2, Send, ShieldCheck, Users } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { createPaymentLink, useProposalDetail } from "@/hooks/use-proposals";
import { formatCurrency, formatDateTime } from "@/lib/i18n";

interface ProposalAnalyticsCardData {
  totalViews: number;
  uniqueViewers: number;
  avgTimeSpentSec: number;
  sectionEngagement: Array<{ sectionId: string; title: string; viewPercentage: number }>;
  viewTimeline: Array<{ date: string; views: number }>;
  lastViewedAt: string | null;
}

function extractSectionText(content: Record<string, unknown>): string[] {
  const blocks = content.blocks;
  if (!Array.isArray(blocks)) {
    return [];
  }

  return blocks
    .map((block) => {
      if (!block || typeof block !== "object") {
        return null;
      }

      const text = (block as { text?: unknown }).text;
      if (typeof text !== "string") {
        return null;
      }

      const trimmed = text.trim();
      return trimmed || null;
    })
    .filter((value): value is string => value !== null);
}

function getSectionKey(sectionId: string, index: number): string {
  return sectionId || `section-${index}`;
}

export default function ProposalDetailPage() {
  const { proposalId } = useParams();
  const { proposal, isLoading } = useProposalDetail(proposalId);
  const [approvalState, setApprovalState] = useState<"idle" | "submitting">("idle");
  const [sendState, setSendState] = useState<"idle" | "submitting">("idle");
  const [signatureState, setSignatureState] = useState<"idle" | "submitting">("idle");
  const [paymentLinkState, setPaymentLinkState] = useState<"idle" | "submitting">("idle");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [signatureRequest, setSignatureRequest] = useState<{ reviewUrl: string; expiresAt: string } | null>(null);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<ProposalAnalyticsCardData | null>(null);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [approvedByUser, setApprovedByUser] = useState(false);
  const [sentByUser, setSentByUser] = useState(false);
  const aiState = approvedByUser ? "approved" : proposal?.aiGenerated ?? "false";
  const isApproved = aiState === "approved";
  const requiresApproval = aiState === "draft";
  const isSent = sentByUser || proposal?.status === "sent";

  const sections = useMemo(() => proposal?.sections ?? [], [proposal?.sections]);
  const resolvedPaymentLinkUrl = paymentLinkUrl ?? proposal?.paymentLinkUrl ?? null;
  const canCreatePaymentLink = (proposal?.totalValue ?? 0) > 0 && proposal?.paymentStatus !== "paid";

  useEffect(() => {
    setPaymentLinkUrl(proposal?.paymentLinkUrl ?? null);
  }, [proposal?.paymentLinkUrl]);

  useEffect(() => {
    if (!proposalId) {
      setAnalytics(null);
      return;
    }

    let cancelled = false;
    setIsAnalyticsLoading(true);
    void apiFetch<{ data: ProposalAnalyticsCardData }>(`/crm/proposals/${proposalId}/analytics`)
      .then((response) => {
        if (!cancelled) {
          setAnalytics(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAnalytics(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAnalyticsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const handleApprove = useCallback(async () => {
    if (!proposalId || !proposal) {
      return;
    }

    setApprovalState("submitting");
    setError(null);
    setSuccess(null);

    try {
      await apiFetch(`/crm/proposals/${proposalId}`, {
        method: "PATCH",
        body: JSON.stringify({ aiGenerated: "approved" }),
      });
      setApprovedByUser(true);
      setSuccess("AI draft approved. The proposal can now be sent.");
    } catch (approvalError) {
      setError(approvalError instanceof Error ? approvalError.message : "Failed to approve AI draft");
    } finally {
      setApprovalState("idle");
    }
  }, [proposal, proposalId]);

  const handleSend = useCallback(async () => {
    if (!proposalId || !proposal) {
      return;
    }

    setSendState("submitting");
    setError(null);
    setSuccess(null);

    try {
      await apiFetch(`/crm/proposals/${proposalId}/send`, { method: "POST" });
      setSentByUser(true);
      setSuccess("Proposal sent from the mounted CRM proposal seam.");
    } catch (sendError) {
      if (sendError instanceof ApiError && sendError.errorCode === "AI_APPROVAL_REQUIRED") {
        setError("Approve the AI draft before sending it to the client.");
      } else {
        setError(sendError instanceof Error ? sendError.message : "Failed to send proposal");
      }
    } finally {
      setSendState("idle");
    }
  }, [proposal, proposalId]);

  const handleRequestSignature = useCallback(async () => {
    if (!proposalId || !proposal) {
      return;
    }

    setSignatureState("submitting");
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch<{ data: { reviewUrl: string; expiresAt: string } }>(`/crm/proposals/${proposalId}/signature-request`, {
        method: "POST",
      });
      setSignatureRequest(response.data);
      setSuccess("Signature request created. Share the signing link below with the recipient.");
    } catch (signatureError) {
      if (signatureError instanceof ApiError && signatureError.errorCode === "AI_APPROVAL_REQUIRED") {
        setError("Approve the AI draft before requesting a signature.");
      } else {
        setError(signatureError instanceof Error ? signatureError.message : "Failed to create signature request");
      }
    } finally {
      setSignatureState("idle");
    }
  }, [proposal, proposalId]);

  const handleCopySignatureLink = useCallback(async () => {
    if (!signatureRequest) {
      return;
    }

    try {
      await navigator.clipboard.writeText(signatureRequest.reviewUrl);
      setSuccess("Signature link copied to clipboard.");
      setError(null);
    } catch {
      setError("Failed to copy the signature link.");
    }
  }, [signatureRequest]);

  const handleCreatePaymentLink = useCallback(async () => {
    if (!proposalId || !proposal) {
      return;
    }

    setPaymentLinkState("submitting");
    setError(null);
    setSuccess(null);

    try {
      const response = await createPaymentLink(proposalId);
      setPaymentLinkUrl(response.paymentLinkUrl);
      setSuccess("Payment link created from the mounted CRM proposal seam.");
    } catch (paymentError) {
      setError(paymentError instanceof Error ? paymentError.message : "Failed to create payment link");
    } finally {
      setPaymentLinkState("idle");
    }
  }, [proposal, proposalId]);

  const handleCopyPaymentLink = useCallback(async () => {
    if (!resolvedPaymentLinkUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(resolvedPaymentLinkUrl);
      setSuccess("Payment link copied to clipboard.");
      setError(null);
    } catch {
      setError("Failed to copy the payment link.");
    }
  }, [resolvedPaymentLinkUrl]);

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Proposal not found</h1>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                The mounted CRM proposal seam did not return a draft for this route.
              </p>
            </div>
          </div>
        </div>
        <Link
          to="/offers"
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
        >
          Back to offers
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-[var(--color-muted)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
              {proposal.status}
            </span>
            {proposal.aiGenerated !== "false" ? (
              <span className="rounded-full bg-[var(--color-ai)]/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-ai)]">
                AI {proposal.aiGenerated}
              </span>
            ) : null}
          </div>
          <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">{proposal.title}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {proposal.personName}
            {" · "}
            {proposal.totalValue != null
              ? formatCurrency(Math.round(Number(proposal.totalValue) * 100), proposal.currency, { maximumFractionDigits: 0 })
              : formatCurrency(0, proposal.currency, { maximumFractionDigits: 0 })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/offers"
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
          >
            Back to offers
          </Link>
          <a
            href={`/offers/${proposal.id}/print?autoprint=1`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
          >
            <Download className="h-4 w-4" />
            Download PDF
          </a>
          {proposal.status === "signed" ? (
            <a
              href={`/crm/proposals/${proposal.id}/signed-document`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
            >
              <Download className="h-4 w-4" />
              Download Signed PDF
            </a>
          ) : null}
          {requiresApproval ? (
            <button
              type="button"
              onClick={handleApprove}
              disabled={approvalState === "submitting"}
              className="btn-press inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              style={{ background: "var(--gradient-ai)" }}
            >
              {approvalState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Approve AI Draft
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSend}
            disabled={sendState === "submitting" || isSent || requiresApproval}
            className="btn-press inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ background: "var(--gradient-energy)" }}
          >
            {sendState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {isSent ? "Already Sent" : "Send Proposal"}
          </button>
          <button
            type="button"
            onClick={handleRequestSignature}
            disabled={signatureState === "submitting" || requiresApproval}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] disabled:opacity-60 hover:bg-[var(--color-muted)]"
          >
            {signatureState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Send for Signature
          </button>
          <button
            type="button"
            onClick={handleCreatePaymentLink}
            disabled={paymentLinkState === "submitting" || !canCreatePaymentLink}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] disabled:opacity-60 hover:bg-[var(--color-muted)]"
          >
            {paymentLinkState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {resolvedPaymentLinkUrl ? "Refresh Payment Link" : "Add Payment Link"}
          </button>
        </div>
      </div>

      {requiresApproval ? (
        <div className="rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Human review required</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                This proposal was AI-generated and is still in draft review. Approve it here before sending it to the client.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {isApproved ? (
        <div className="rounded-2xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-[var(--color-success)]" />
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Human-approved draft</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                The AI-assisted content has been reviewed and approved. Sending now uses the mounted CRM proposal route.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-3 text-sm text-[var(--color-success)]">
          {success}
        </div>
      ) : null}

      {signatureRequest ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Live signing link</h2>
              <p className="break-all text-sm text-[var(--color-muted-foreground)]">{signatureRequest.reviewUrl}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Expires {formatDateTime(signatureRequest.expiresAt)}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleCopySignatureLink()}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
              >
                <Copy className="h-4 w-4" />
                Copy link
              </button>
              <a
                href={signatureRequest.reviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
              >
                <ExternalLink className="h-4 w-4" />
                Open signing page
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {resolvedPaymentLinkUrl || proposal.paymentStatus === "paid" ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Payment collection</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Status: <span className="font-medium capitalize text-[var(--color-foreground)]">{proposal.paymentStatus}</span>
              </p>
              {resolvedPaymentLinkUrl ? (
                <p className="break-all text-sm text-[var(--color-muted-foreground)]">{resolvedPaymentLinkUrl}</p>
              ) : (
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  This proposal is already marked paid and no new payment link is required.
                </p>
              )}
            </div>
            {resolvedPaymentLinkUrl ? (
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void handleCopyPaymentLink()}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                >
                  <Copy className="h-4 w-4" />
                  Copy link
                </button>
                <a
                  href={resolvedPaymentLinkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open payment page
                </a>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Proposal Analytics</h2>
        </div>

        {isAnalyticsLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" />
          </div>
        ) : analytics ? (
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  <Eye className="h-4 w-4" />
                  Total views
                </div>
                <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{analytics.totalViews}</p>
              </div>
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  <Users className="h-4 w-4" />
                  Unique viewers
                </div>
                <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{analytics.uniqueViewers}</p>
              </div>
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  <Clock3 className="h-4 w-4" />
                  Avg dwell
                </div>
                <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{analytics.avgTimeSpentSec}s</p>
              </div>
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  <BarChart3 className="h-4 w-4" />
                  Last viewed
                </div>
                <p className="mt-2 text-sm font-medium text-[var(--color-foreground)]">
                  {analytics.lastViewedAt ? formatDateTime(analytics.lastViewedAt) : "Not viewed yet"}
                </p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <h3 className="text-sm font-semibold text-[var(--color-foreground)]">View timeline</h3>
                {analytics.viewTimeline.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {analytics.viewTimeline.map((entry) => (
                      <div key={entry.date} className="flex items-center justify-between text-sm text-[var(--color-muted-foreground)]">
                        <span>{entry.date}</span>
                        <span className="font-medium text-[var(--color-foreground)]">{entry.views} views</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No view events recorded yet.</p>
                )}
              </div>

              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Engagement depth</h3>
                {analytics.sectionEngagement.length > 0 ? (
                  <div className="mt-3 space-y-3">
                    {analytics.sectionEngagement.map((entry) => (
                      <div key={entry.sectionId} className="space-y-1">
                        <div className="flex items-center justify-between text-sm text-[var(--color-muted-foreground)]">
                          <span>{entry.title}</span>
                          <span className="font-medium text-[var(--color-foreground)]">{entry.viewPercentage}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-[var(--color-muted)]">
                          <div
                            className="h-2 rounded-full bg-[var(--color-primary)]"
                            style={{ width: `${Math.max(0, Math.min(entry.viewPercentage, 100))}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No engagement samples recorded yet.</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted-foreground)]">Proposal analytics are unavailable right now.</p>
        )}
      </div>

      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Proposal Sections</h2>
        </div>

        <div className="space-y-4">
          {sections.map((section, index) => {
            const paragraphs = extractSectionText(section.content);

            return (
              <section
                key={getSectionKey(section.id, index)}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-[var(--color-foreground)]">{section.title}</h3>
                    <p className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                      {section.sectionType}
                    </p>
                  </div>
                </div>
                <div className="space-y-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
                  {paragraphs.length > 0 ? (
                    paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
                  ) : (
                    <p>No structured content blocks were returned for this section.</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
